import { isEmpty } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import PermissionGuard from 'src/components/guards/PermissionGuard';
import {
  CancelPoPermissions,
  ClosePoPermissions,
  CompleteInvoicePermissions,
  DeleteInvoicePermissions,
  UnDeleteInvoicePermissions,
} from 'src/constants/permissionsList';
import useConfirm from 'src/hooks/useConfirm';
import useViewInvoices from 'src/hooks/useViewInvoices';
import useCancelPoApi from 'src/services/useCancelPoApi';
import useClosePoApi from 'src/services/useClosePOApi';
import useCompleteInvoiceApi from 'src/services/useCompleteInvoiceApi';
import useDeleteInvoicesApi from 'src/services/useDeleteInvoicesApi';
import useUnDeleteInvoicesApi from 'src/services/useUnDeleteInvoicesApi';
import { IInvoiceListItem } from 'src/types/Invoice';
import ActionButton from './ActionButton';

interface Props {
  selectedRows: IInvoiceListItem[];
  setRows: React.Dispatch<React.SetStateAction<IInvoiceListItem[]>>;
  refresh: VoidFunction;
}

const ActionButtons: FC<Props> = ({ selectedRows, setRows, refresh }) => {
  const { completeInvoice, completeCallLoading } = useCompleteInvoiceApi();
  const { cancelPo, cancelPoCallLoading } = useCancelPoApi();
  const { deleteInvoices, deletedInvoicesLoading } = useDeleteInvoicesApi();
  const { setCompleteApiResponseData } = useViewInvoices();
  const { unDeleteInvoices, unDeleteInvoiceLoading } = useUnDeleteInvoicesApi();
  const { confirm } = useConfirm();
  const { closePo, closePoCallLoading } = useClosePoApi();

  const isAnyRowSelected = useMemo(() => !!selectedRows.length, [selectedRows.length]);

  const { isClosePODisabled, invalidClosePoIds } = useMemo(() => {
    const invalidClosePoIds = selectedRows.filter((row) => !row.canClosePo).map((row) => row.poNum);
    return {
      isClosePODisabled: !isAnyRowSelected || !!invalidClosePoIds.length,
      invalidClosePoIds: invalidClosePoIds,
    };
  }, [isAnyRowSelected, selectedRows]);

  const { isCompleteDisabled, invalidCompleteInvoiceNumbers } = useMemo(() => {
    const invalidCompleteInvoiceNumbers = selectedRows
      .filter((row) => !row.canComplete)
      .map((row) => row.invoiceNumber);

    return {
      isCompleteDisabled: !isAnyRowSelected || !!invalidCompleteInvoiceNumbers.length,
      invalidCompleteInvoiceNumbers,
    };
  }, [isAnyRowSelected, selectedRows]);

  const { isDeleteDisabled, invalidDeleteInvoiceNumbers } = useMemo(() => {
    const invalidDeleteInvoiceNumbers = selectedRows
      .filter((row) => !row.canDelete)
      .map((row) => row.invoiceNumber);

    return {
      isDeleteDisabled: !isAnyRowSelected || !!invalidDeleteInvoiceNumbers.length,
      invalidDeleteInvoiceNumbers,
    };
  }, [isAnyRowSelected, selectedRows]);

  const { isUndeleteDisabled, invalidUndeleteInvoiceNumbers } = useMemo(() => {
    const invalidUndeleteInvoiceNumbers = selectedRows
      .filter((row) => !row.canUndelete)
      .map((row) => row.invoiceNumber);

    return {
      isUndeleteDisabled: !isAnyRowSelected || !!invalidUndeleteInvoiceNumbers.length,
      invalidUndeleteInvoiceNumbers,
    };
  }, [isAnyRowSelected, selectedRows]);

  const { isCancelPODisabled, invalidCancelPOIds } = useMemo(() => {
    const invalidCancelPOIds = selectedRows
      .filter((row) => !row.canCancelPo)
      .map((row) => row.poNum);

    return {
      isCancelPODisabled: !isAnyRowSelected || !!invalidCancelPOIds.length,
      invalidCancelPOIds,
    };
  }, [isAnyRowSelected, selectedRows]);

  const refreshList = useCallback(() => {
    refresh();
    setRows((prev) => prev.map((row) => ({ ...row, selected: false })));
  }, [refresh, setRows]);

  const handleCompleteDocument = useCallback(async () => {
    confirm({
      title: 'Confirm Complete',
      content: (
        <p>
          This Invoice will be Completed. You will not be able to edit the invoice once you perform
          this action. Do you wish to continue?
        </p>
      ),
      confirmButtonText: 'Confirm',
      confirmColor: 'info',
    }).then(async (isCompleteConfirmed) => {
      if (isCompleteConfirmed) {
        try {
          const selectedInvoiceIds = selectedRows.map((row: IInvoiceListItem) => row.receiptId);
          const response = await completeInvoice({ invoiceIds: selectedInvoiceIds });
          setCompleteApiResponseData(response);
          const { unProcessed = [], processedIds = [] } = response || {};
          let title = '⚠ Document Complete Error';
          let content = '';
          if (isEmpty(unProcessed)) {
            title = 'Documents Completed';
            content = 'All documents completed successfully';
          } else if (isEmpty(processedIds)) {
            content =
              'We were unable to complete any of the selected documents. Please address issues on the individual record details page to complete the document.';
          } else {
            content =
              "We successfully completed some documents, but one or more of the selected documents couldn't be completed. Please resolve any issues on the individual record details page to complete the document.";
          }
          confirm({
            title,
            content: (
              <div>
                <div>{content}</div>
              </div>
            ),
            cancelButtonText: 'Close',
            cancelColor: 'info',
            hasNoConfirmButton: true,
            width: '600px',
          });
        } catch (e) {
          console.error(e);
        } finally {
          refreshList();
        }
      }
    });
  }, [completeInvoice, confirm, refreshList, selectedRows, setCompleteApiResponseData]);

  const handleDelete = useCallback(async () => {
    confirm({
      title: 'Delete Document(s)',
      content: <p>Are you sure you want to Delete the selected document(s)?</p>,
      confirmButtonText: 'OK',
      confirmColor: 'info',
    }).then(async (isDeleteConfirmed) => {
      if (isDeleteConfirmed) {
        try {
          const selectedInvoiceIds = selectedRows.map((row: IInvoiceListItem) => row.receiptId);
          await deleteInvoices({ invoiceIds: selectedInvoiceIds });
        } catch (e) {
          console.error(e);
        } finally {
          refreshList();
        }
      }
    });
  }, [confirm, deleteInvoices, refreshList, selectedRows]);

  const handleUndelete = useCallback(async () => {
    confirm({
      title: 'Undelete',
      content: <p>Are you sure you want to continue?</p>,
      confirmButtonText: 'OK',
      confirmColor: 'info',
    }).then(async (isUndeleteConfirmed) => {
      if (isUndeleteConfirmed) {
        try {
          const selectedInvoiceIds = selectedRows.map((row: IInvoiceListItem) => row.receiptId);
          await unDeleteInvoices({ invoiceIds: selectedInvoiceIds });
        } catch (e) {
          console.error(e);
        } finally {
          refreshList();
        }
      }
    });
  }, [confirm, refreshList, selectedRows, unDeleteInvoices]);

  const handleCancelPO = useCallback(async () => {
    confirm({
      title: 'Cancel PO',
      content: <p>Are you sure you want to continue?</p>,
      confirmButtonText: 'OK',
      confirmColor: 'info',
    }).then(async (isCancelPoConfirmed) => {
      if (isCancelPoConfirmed) {
        const selectedPoIds = selectedRows.map((row: IInvoiceListItem) => row.poId);
        try {
          await cancelPo({ poIds: selectedPoIds });
        } catch (e) {
          console.error(e);
        } finally {
          refreshList();
        }
      }
    });
  }, [cancelPo, confirm, refreshList, selectedRows]);

  const handleClosePO = useCallback(() => {
    confirm({
      title: 'Close PO',
      content: <p>Are you sure you want to continue?</p>,
      confirmButtonText: 'OK',
      confirmColor: 'info',
    }).then(async (isClosePoConfirmed) => {
      if (isClosePoConfirmed) {
        try {
          const selectedPoIds = selectedRows.map((row: IInvoiceListItem) => row.poId);
          const response = await closePo({ poIds: selectedPoIds });
          if (response.data.processedIds.length) {
            confirm({
              title: 'Close PO',
              content: (
                <p>
                  {
                    'Selected PO(s) successfully closed. Any variances calculated can be viewed under Reports -> View Variances'
                  }
                </p>
              ),
              hasNoConfirmButton: true,
              cancelButtonText: 'OK',
            });
          }
        } catch (e) {
          console.error(e);
        } finally {
          refreshList();
        }
      }
    });
  }, [confirm, selectedRows, closePo, refreshList]);

  const buttons = useMemo(
    () =>
      [
        {
          text: 'Close PO',
          color: 'secondary',
          onClick: handleClosePO,
          disabled: isClosePODisabled,
          permissions: ClosePoPermissions,
          invalidIds: invalidClosePoIds,
          type: 'PO',
          isLoading: closePoCallLoading,
        },
        {
          text: 'Complete Document',
          color: 'primary',
          onClick: handleCompleteDocument,
          disabled: isCompleteDisabled,
          permissions: CompleteInvoicePermissions,
          invalidIds: invalidCompleteInvoiceNumbers,
          type: 'Doc',
          isLoading: completeCallLoading,
        },
        {
          text: 'Delete',
          color: 'error',
          onClick: handleDelete,
          disabled: isDeleteDisabled,
          permissions: DeleteInvoicePermissions,
          invalidIds: invalidDeleteInvoiceNumbers,
          type: 'Doc',
          isLoading: deletedInvoicesLoading,
        },
        {
          text: 'Undelete',
          color: 'dark',
          onClick: handleUndelete,
          disabled: isUndeleteDisabled,
          permissions: UnDeleteInvoicePermissions,
          invalidIds: invalidUndeleteInvoiceNumbers,
          type: 'Doc',
          isLoading: unDeleteInvoiceLoading,
        },
        {
          text: 'Cancel PO',
          color: 'error',
          onClick: handleCancelPO,
          disabled: isCancelPODisabled,
          permissions: CancelPoPermissions,
          invalidIds: invalidCancelPOIds,
          type: 'PO',
          isLoading: cancelPoCallLoading,
        },
      ] as const,
    [
      handleClosePO,
      isClosePODisabled,
      invalidClosePoIds,
      closePoCallLoading,
      handleCompleteDocument,
      isCompleteDisabled,
      invalidCompleteInvoiceNumbers,
      completeCallLoading,
      handleDelete,
      isDeleteDisabled,
      invalidDeleteInvoiceNumbers,
      deletedInvoicesLoading,
      handleUndelete,
      isUndeleteDisabled,
      invalidUndeleteInvoiceNumbers,
      unDeleteInvoiceLoading,
      handleCancelPO,
      isCancelPODisabled,
      invalidCancelPOIds,
      cancelPoCallLoading,
    ]
  );

  return (
    <div className="flex flex-wrap">
      {buttons.map((btn) => (
        <PermissionGuard permissions={btn?.permissions} or key={btn.text}>
          <ActionButton btn={btn} selectedInvoices={selectedRows} key={btn.text} />
        </PermissionGuard>
      ))}
    </div>
  );
};

export default ActionButtons;
